import { Link } from "react-router";
import { useLocale } from "../locale/locale-context.tsx";

export function NotFoundPage() {
  const { t } = useLocale();
  return (
    <ErrorShell
      watermark="404"
      heading={t("errors.404.title")}
      subtitle={t("errors.404.subtitle")}
      cta={t("errors.404.cta")}
      ctaHref="/"
    />
  );
}

interface ErrorShellProps {
  watermark: string;
  heading: string;
  subtitle: string;
  cta: string;
  ctaHref: string;
}

// Shared shell for the 404 / 500 / maintenance pages. Re-uses the soft-blush
// gradient + corner radial blooms from the admin login so the brand surface
// stays consistent everywhere a user lands on an "off-happy-path" page.
export function ErrorShell({ watermark, heading, subtitle, cta, ctaHref }: ErrorShellProps) {
  return (
    <div className="relative flex min-h-[calc(100vh-4rem)] items-center justify-center overflow-hidden bg-gradient-to-br from-[#fff8fa] via-[#fce6ee] to-[#f4cfdc] px-6 py-16">
      {/* Anchor corner blooms — same two radial gradients as the auth pages. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 [background:radial-gradient(45rem_30rem_at_15%_10%,rgba(247,200,213,0.55),transparent_60%),radial-gradient(55rem_40rem_at_95%_95%,rgba(233,75,122,0.18),transparent_65%)]"
      />

      {/* Oversized tilted watermark sits behind the card.
          Pulled into its own absolutely-positioned div so the card layout
          stays unaffected if the watermark text changes width. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <span className="-rotate-12 select-none font-display text-[18rem] leading-none tracking-tighter text-[var(--brand-rose)]/[0.10] sm:text-[22rem]">
          {watermark}
        </span>
      </div>

      <div className="relative w-full max-w-md text-center">
        <div className="rounded-[28px] bg-white/80 p-10 shadow-[0_40px_80px_-32px_rgba(233,75,122,0.22)] backdrop-blur-sm">
          <h1 className="font-heading text-3xl font-bold leading-tight tracking-tight text-[var(--brand-ink)] sm:text-4xl">
            {heading}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-[var(--brand-muted)]">{subtitle}</p>
          <Link
            to={ctaHref}
            className="mt-8 inline-flex h-11 items-center justify-center rounded-xl bg-[var(--brand-rose)] px-6 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(233,75,122,0.55)] transition-colors hover:bg-[var(--brand-rose)]/90"
          >
            {cta}
          </Link>
        </div>
      </div>
    </div>
  );
}
